import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useWarmTheme } from '../../../theme/ThemeContext';
import { useLauncher } from '../useLauncher';
import { TOOLS, TOOL_BY_KEY } from '../toolRegistry';
import { buildOrderedGroups } from '../sidebarShared';
import NotificationsBell from '../../NotificationsBell';
import { Sprite } from './Icons';
import { DwSidebar, DwTopBar } from './Shell';
import { Scrim, Palette, KeysModal, FavModal } from './Overlays';
import '../../../styles/derwent-home.css';
import '../../../styles/derwent-page.css';

// Reusable DerwentAI shell (sidebar + top bar + ⌘K palette) so every tool page shares the Home design.
const DwShell = ({ activeKey, children }) => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleMode } = useWarmTheme();
  const { allowed, favourites, launch, groupOrder, toolOrder, saveFavourites } = useLauncher();
  const groups = useMemo(() => buildOrderedGroups(allowed, groupOrder, toolOrder), [allowed, groupOrder, toolOrder]);
  const allowedTools = useMemo(() => TOOLS.filter((t) => allowed.has(t.key)), [allowed]);
  const favTools = useMemo(() => (favourites || []).map((k) => TOOL_BY_KEY[k]).filter(Boolean), [favourites]);
  const [overlay, setOverlay] = useState(null);
  const [clock, setClock] = useState('');
  useEffect(() => { const f = () => setClock(new Date().toLocaleTimeString('en-AU', { timeZone: 'Australia/Hobart', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })); f(); const t = setInterval(f, 1000); return () => clearInterval(t); }, []);
  const closeAll = useCallback(() => setOverlay(null), []);
  const go = useCallback((p) => { closeAll(); navigate(p); }, [navigate, closeAll]);
  const openAndi = useCallback((message) => { closeAll(); window.dispatchEvent(new CustomEvent('hey-andi:open', { detail: message ? { message } : undefined })); }, [closeAll]);
  const runCommand = useCallback((kind, arg) => {
    if (kind === 'andi') openAndi();
    else if (kind === 'theme') toggleMode();
    else if (kind === 'keys') setOverlay('keys');
    else if (kind === 'go') go(arg);
    else if (kind === 'launch') { closeAll(); launch(arg); }
    else if (kind === 'task') go('/');
  }, [openAndi, toggleMode, go, launch, closeAll]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOverlay((o) => (o === 'palette' ? null : 'palette')); return; }
      if (e.key === 'Escape' && overlay) { closeAll(); return; }
      const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '') || document.activeElement?.isContentEditable;
      if (typing || overlay || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') setOverlay('keys');
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [overlay, closeAll]);

  return (
    <div className="dwh" data-theme={mode === 'dark' ? 'dark' : 'light'} data-testid="dw-shell">
      <Sprite />
      <div className="app">
        <DwSidebar user={user} favTools={favTools} groups={groups} act={activeKey} onNav={go} onLaunch={(t) => { closeAll(); launch(t); }} onLogout={async () => { await logout(); navigate('/login'); }} open={overlay === 'side'} onClose={closeAll} token={token} onEditFav={() => setOverlay('fav')} />
        <div className="main">
          <DwTopBar clock={clock} theme={mode} onPalette={() => setOverlay('palette')} onTheme={toggleMode} onKeys={() => setOverlay('keys')} onMenu={() => setOverlay('side')} bell={<NotificationsBell />} />
          <div className="page">{children}</div>
        </div>
      </div>
      <Scrim on={!!overlay} onClick={closeAll} />
      <FavModal on={overlay === 'fav'} onClose={closeAll} tools={allowedTools} favourites={favourites || []} onSave={saveFavourites} />
      <Palette on={overlay === 'palette'} onClose={closeAll} queue={[]} tools={allowedTools} token={token} run={runCommand} onAskAndi={(t) => openAndi(t)} />
      <KeysModal on={overlay === 'keys'} onClose={closeAll} />
    </div>
  );
};

export default DwShell;
